import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { toast } from "react-hot-toast";
import config from "../../config";
import { nationalDigits } from "../../constants";
import {
  getNotificationEvents,
  getUserNotificationSettings,
  saveUserNotificationSetting,
  getUserProfile,
  Logout,
} from "../../api";

const initialState = {
  user: null,
  userLoading: true,
  isPCUser: false,
  isPCAdmin: false,
  isKurvUser: false,
  isAdmin: false,
  isAgent: false,
  isViewResiduals: false,
  userOrgIsPaychex: false,
  userOrgIsEMS: false,
  isEntitySuperAdmin: false,
  isDashboardAllowedOrgAdmin: false,
  canViewResidualData: false,
  isGodUser: false,
  organizationId: null,
  isV2Enabled: false,
  //Note: Putting notifications here since it's related to a user
  // If notification events become more complex, refactor this.
  notificationEvents: [],
  notificationEventsLoading: true,
  notificationEventsError: null,
  notificationSettings: [],
  notificationSettingsLoading: true,
  notificationSettingsError: null,

  // Modal states
  resetPasswordModal: false,
  newPasswordModal: false,
  personalDetailsModal: false,
  apiTokensModal: false,
  twoFactorModal: false,
  phoneVerificationModal: false,
  // National digits when the modal is opened for a number the user has already typed
  // somewhere else — the personal details form hands its new number over.
  phoneVerificationPrefill: null,

  userProfile: null,
};

const useUserStore = create(
  devtools((set, get) => ({
    ...initialState,
    setUserLoading: (loading) => set({ userLoading: loading }),
    setUser: (user) =>
      set({
        user,
        organizationId: user?.organization_id,
        agentUrl: user?.agent_url,
        isAgent: user?.organization_id > 1 && user?.role === "agent",
        isEMSUser: user?.is_ems_user,
        isAdmin: user?.role === "admin",
        isPCAdmin: user?.is_pc_admin,
        isEMSPCAdmin: user?.is_ems_pc_admin,
        isViewResiduals: user?.is_view_residuals,
        isV2Enabled: user?.is_v2_enable,
        userOrgIsPaychex:
          user?.organization_id == config.paymentcloud.PAYCHEX_ORG_ID,
        userOrgIsEMS: user?.organization_id == config.paymentcloud.EMS_ORG_ID,
        isPCUser: user?.organization_id === 1,
        isKurvUser: user?.organization_id == config.paymentcloud.KURVPAY_ORG_ID,
        isKurvPayEntity: user?.organization?.ems_entity === "kurvpay",
        isEntitySuperAdmin:
          (user?.organization_id == config.paymentcloud.PAYMENTCLOUD_ORG_ID ||
            user?.organization_id == config.paymentcloud.EMS_ORG_ID ||
            user?.organization_id == config.paymentcloud.KURVPAY_ORG_ID) &&
          user?.role === "admin",
        // v2BetaFunctionality() from the server: also admits every member of the
        // PaymentCloud / KurvPay orgs, not just admins/agents of a replica-linked org.
        isDashboardAllowedOrgAdmin: !!user?.can_access_dashboard_modules,
        // Mirrors isConfiguredForResidualData() on the server: the
        // is_view_residual toggle for every role, and for an agent a positive
        // split and a rep ID on top. Gate anything that renders residual
        // figures on this, not on replica_org_id.
        canViewResidualData: !!user?.can_view_residual_data,
        isGodUser: !!user?.is_god_user,
        userLoading: false,
        ems_entity: user?.organization?.ems_entity,
        entity: user?.organization?.ems_entity,
      }),
    resetNotificationSettingsPage: () =>
      set({
        notificationEvents: [],
        notificationEventsLoading: true,
        notificationEventsError: null,
        notificationSettings: [],
        notificationSettingsLoading: true,
        notificationSettingsError: null,
      }),
    logout: () => set({ ...initialState }),
    setResetPasswordModal: (t) => set({ resetPasswordModal: t }),
    setNewPasswordModal: (t) => set({ newPasswordModal: t }),
    setPersonalDetailsModal: (t) => set({ personalDetailsModal: t }),
    setApiTokensModal: (t) => set({ apiTokensModal: t }),
    setTwoFactorModal: (t) => set({ twoFactorModal: t }),
    // The modal itself lives in routes.js, next to the other app-level modals.
    setPhoneVerificationModal: (t, prefill = null) =>
      set({ phoneVerificationModal: t, phoneVerificationPrefill: t ? prefill : null }),
  })),
);

export const fetchNotificationEvents = async (
  abortController,
  setInitialLoading = true,
) => {
  // ToDo: Optimisic UI in a proper way, below is a temporary solution
  if (setInitialLoading) {
    useUserStore.setState({ notificationEventsLoading: true });
  }

  try {
    const res = await getNotificationEvents(abortController);

    if (!res?.success) {
      useUserStore.setState({
        notificationEventsError:
          res?.message || "Error fetching notification events",
        notificationEventsLoading: false,
      });
      return;
    }

    useUserStore.setState({
      notificationEvents: res.data,
      notificationEventsLoading: false,
      notificationEventsError: null,
    });
  } catch (err) {
    if (err.name === "AbortError") {
      return;
    }
    console.error("Notification events fetch error:", err);
    useUserStore.setState({
      notificationEventsError:
        err.message || "Error fetching notification events",
      notificationEventsLoading: false,
    });
  }
};

export const fetchUserNotificationSettings = async (abortController) => {
  try {
    const res = await getUserNotificationSettings(abortController);

    if (!res?.success) {
      useUserStore.setState({
        notificationSettingsError:
          res?.message || "Error fetching user notification settings",
        notificationSettingsLoading: false,
      });
      return;
    }

    useUserStore.setState({
      notificationSettings: res.data,
      notificationSettingsLoading: false,
      notificationSettingsError: null,
    });
  } catch (err) {
    if (err.name === "AbortError") {
      return;
    }
    console.error("User notification settings fetch error:", err);
    useUserStore.setState({
      notificationSettingsError:
        err.message || "Error fetching user notification settings",
      notificationSettingsLoading: false,
    });
  }
};

export const updateUserNotificationSetting = async (data, abortController) => {
  try {
    const res = await saveUserNotificationSetting(data, abortController);
    if (!res?.success) {
      useUserStore.setState({
        notificationSettingsError:
          res?.message || "Error updating user notification settings",
        notificationSettingsLoading: false,
      });
      return;
    }

    toast.dismiss();
    toast.success("Notification settings updated");
  } catch (err) {
    if (err.name === "AbortError") {
      return;
    }
    console.error("User notification settings update error:", err);
    toast.dismiss();
    if (err?.status === 422 && err?.data) {
      const errorData = err.data;
      let errorMessage = errorData.message || "Validation error";
      const fieldErrors = errorData.errors
        ? Object.values(errorData.errors).flat()
        : [];

      useUserStore.setState({
        notificationSettingsLoading: false,
      });

      if (fieldErrors.length > 0) {
        errorMessage = `${fieldErrors[0]}`;
      }

      toast.error(errorMessage);
      return;
    }

    toast.error("Error updating user notification settings");
  }
};

export const fetchUserProfile = async () => {
  const response = await getUserProfile();

  if (response.success) {
    const name =
      response?.data?.name && response?.data?.name !== undefined
        ? response?.data?.name?.split(" ")
        : [];
    const address = response?.data?.address?.split(",");

    useUserStore.setState({
      userProfile: {
        first_name: name && name[0] !== undefined ? name[0] : "",
        last_name: name && name[1] !== undefined ? name[1] : "",
        direct_phone: response?.data?.phone_direct || "",
        // Stored as E.164 by the verification flow; the form shows national digits.
        cell_phone: nationalDigits(response?.data?.phone_cell),
        email: response?.data?.email || "",
        address: address && address[0] !== undefined ? address[0] : "",
        city: address && address[1] !== undefined ? address[1] : "",
        state:
          address && address[2] !== undefined
            ? { label: address[2], value: address[2] }
            : "",
        zip_code: address && address[3] !== undefined ? address[3] : "",
        signature_first_name: name && name[0] !== undefined ? name[0] : "",
        signature_last_name: name && name[1] !== undefined ? name[1] : "",
      },
    });
  }
};

export const userProfileDropdownOptions = (history) => [
  {
    text: "Notification settings",
    type: "normal",
    onClick: () => {
      history.push("/v2/profile/notification-settings");
    },
  },
  {
    text: "Change Personal Details",
    type: "normal",
    onClick: () => {
      useUserStore.getState().setPersonalDetailsModal(true);
    },
  },
  {
    text: "Reset password",
    type: "normal",
    onClick: () => {
      useUserStore.getState().setResetPasswordModal(true);
    },
  },
  {
    text: "API Tokens",
    type: "normal",
    onClick: () => {
      useUserStore.getState().setApiTokensModal(true);
    },
  },
  {
    text: "Two-Factor Auth",
    type: "normal",
    onClick: () => {
      useUserStore.getState().setTwoFactorModal(true);
    },
  },
  {
    text: "Phone Number",
    type: "normal",
    // Dropped entirely when the master switch is off (config/otp.php) — the
    // endpoints behind it refuse, so the entry would only lead to an error.
    hidden: useUserStore.getState().user?.phone_verification_enabled === false,
    onClick: () => {
      useUserStore.getState().setPhoneVerificationModal(true);
    },
  },
  {
    text: "Logout",
    type: "normal",
    onClick: () => {
      Logout()
        .then((response) => {
          window.location = "/";
        })
        .catch((error) => {});
    },
  },
]
  // The Phone Number entry drops out when the master switch is off.
  .filter((option) => !option.hidden);

export default useUserStore;
